import { ReactNode, forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { twMerge } from '@/utils/twMerge';

export type Props = {
  prefixCls?: string;
  type?: string;
  disabled?: boolean;
  placeholder?: string;
  onChange?: (e) => void;
  onBlur?: (e) => void;
  onFocus?: (e) => void;
  onInput?: (e) => void;
  onCopy?: (e) => void;
  onKeyDown?: (e) => void;
  extra?: string;
  value?: string | number;
  prefix?: ReactNode | ReactNode[];
  readonly?: boolean;
  autoFocus?: boolean;
  defaultValue?: string | number;
  error?: boolean;
  className?: string;
  inputClassName?: string;
  prefixClassName?: string;
  size?: string;
  maxLength?: number;
  min?: number;
  max?: number;
  autoComplete?: string;
  spellCheck?: boolean;
  id?: string;
  name?: string;
};

const Input = (props: Props, ref) => {
  const {
    type = 'text',
    value,
    defaultValue,
    placeholder,
    readonly,
    disabled,
    onChange,
    onBlur,
    onFocus,
    onKeyDown,
    onInput,
    onCopy,
    error,
    className,
    inputClassName,
    prefix,
    autoFocus,
    maxLength,
    size,
    autoComplete,
    spellCheck,
    prefixClassName,
    ...rest
  } = props;

  const inputRef = useRef<HTMLInputElement>(null);
  const [currentValue, setCurrentValue] = useState(value ?? defaultValue ?? '');

  useEffect(() => {
    if (value !== undefined && value !== null) {
      setCurrentValue(value);
    }
  }, [value]);

  const handleValue = val => {
    let _val = val;
    if (_val > maxLength) {
      _val = _val.toString().slice(0, maxLength);
    }
    setCurrentValue(_val);
    return _val;
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let _val = event.target.value;
    _val = handleValue(_val);
    if (onChange) {
      onChange(_val);
    }
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    if (onBlur) {
      onBlur(event);
    }
  };

  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    if (onFocus) {
      onFocus(event);
    }
  };

  const handlekey = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (onKeyDown && (event.code === 'Enter' || event.keyCode === 13)) {
      event.code === 'Enter' && onKeyDown(event);
    }
  };

  const handleInputChange = event => {
    let _val = event.target.value;
    _val = handleValue(_val);
    if (onInput) {
      onInput(_val);
    }
  };

  const handleCopy = event => {
    if (onCopy) {
      onCopy(event.target.value);
    }
  };

  useImperativeHandle(ref, () => ({
    focus: () => {
      inputRef.current?.focus();
    },
    blur: () => {
      inputRef.current?.blur();
    }
  }));

  return (
    <div
      className={twMerge(
        'relative flex items-center pl-[15px] min-h-[44px] bg-surface-100-bg overflow-hidden',
        className
      )}>
      <div className="pr-[15px] flex items-center flex-1 self-stretch overflow-hidden">
        {prefix && (
          <div
            className={twMerge(
              'ml-0 mr-[5px] inline-flex items-center text-left whitespace-nowrap overflow-hidden',
              prefixClassName
            )}>
            {prefix}
          </div>
        )}
        <div className="flex-1 overflow-hidden">
          <input
            ref={inputRef}
            className={twMerge(
              'w-full leading-none enki-body-2xs py-[2px] px-0 border-none outline-none bg-transparent appearance-none text-surface-100-fg-default text-[17px] focus-visible:outline-none focus:outline-none',
              inputClassName
            )}
            type={type}
            value={currentValue}
            aria-label={placeholder}
            placeholder={placeholder}
            readOnly={readonly}
            disabled={disabled}
            onChange={handleChange}
            onBlur={handleBlur}
            onFocus={handleFocus}
            onInput={handleInputChange}
            onCopy={handleCopy}
            onKeyDown={handlekey}
            onKeyPress={handlekey}
            autoFocus={autoFocus}
            maxLength={props.maxLength}
            max={props.max}
            min={props.min}
            autoComplete={autoComplete}
            spellCheck={spellCheck}
            {...rest}
          />
        </div>
      </div>
    </div>
  );
};

export default forwardRef(Input);
